import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { questionsAPI, nodesAPI, votesAPI, commentsAPI, usersAPI } from '../lib/api';
import { Header } from '../components/layout/Header';
import { MindmapViewer } from '../components/mindmap/MindmapViewer';
import { RichTextViewer } from '../components/editor/RichTextEditor';
import { ShareButton } from '../components/sharing/ShareButton';
import { FollowButton } from '../components/follow/FollowButton';
import { SEOHead, MindmapStructuredData } from '../components/seo/SEOHead';
import { MentionInput, renderTextWithMentions } from '../components/comments/MentionInput';
import { RelatedMindmaps } from '../components/related/RelatedMindmaps';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { 
  ArrowLeft, 
  Users, 
  GitBranch, 
  Plus,
  MessageSquare,
  X,
  Send,
  FileText,
  Trash2,
  Link2,
  LogIn,
  Calendar,
  User,
  Info,
  Compass,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export default function QuestionDetailPage() {
  const { questionId } = useParams();
  const [searchParams] = useSearchParams();
  const highlightNodeId = searchParams.get('node');
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [question, setQuestion] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userVotes, setUserVotes] = useState({});
  
  // Add Node Modal
  const [showAddNode, setShowAddNode] = useState(false);
  const [parentNodeId, setParentNodeId] = useState(null);
  const [newNodeTitle, setNewNodeTitle] = useState('');
  const [addingNode, setAddingNode] = useState(false);
  
  // Comments Sheet
  const [showComments, setShowComments] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [mentionUsers, setMentionUsers] = useState([]);
  
  // Content Sheet (One-pager)
  const [showContent, setShowContent] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  
  // Active tab for question info panel
  const [activeInfoTab, setActiveInfoTab] = useState('overview');

  const fetchQuestionData = useCallback(async () => {
    setLoading(true);
    try {
      const [questionRes, nodesRes] = await Promise.all([
        questionsAPI.getOne(questionId),
        nodesAPI.getByQuestion(questionId),
      ]);
      
      setQuestion(questionRes.data);
      setNodes(nodesRes.data);
      
      // Fetch user votes if authenticated
      if (isAuthenticated) {
        const votes = {};
        for (const node of nodesRes.data) {
          try {
            const voteRes = await votesAPI.getUserVote(node.node_id);
            if (voteRes.data.vote_type) {
              votes[node.node_id] = voteRes.data.vote_type;
            }
          } catch (e) {
            // Ignore vote fetch errors
          }
        }
        setUserVotes(votes);
      }
    } catch (error) {
      console.error('Error fetching question:', error);
      toast.error('Failed to load mindmap');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [questionId, isAuthenticated, navigate]);

  useEffect(() => {
    fetchQuestionData();
  }, [fetchQuestionData]);
  
  // Fetch users for mention suggestions
  useEffect(() => {
    const fetchMentionUsers = async () => {
      try {
        const response = await usersAPI.searchForMention('', questionId);
        setMentionUsers(response.data.users || []);
      } catch (error) {
        console.error('Error fetching mention users:', error);
      }
    };
    
    if (questionId) {
      fetchMentionUsers();
    }
  }, [questionId]);

  const handleAddChild = useCallback((nodeId) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to add nodes');
      return;
    }
    setParentNodeId(nodeId);
    setNewNodeTitle('');
    setShowAddNode(true);
  }, [isAuthenticated]);

  const handleSubmitNode = async () => {
    if (!newNodeTitle.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    setAddingNode(true);
    try {
      const response = await nodesAPI.create({
        title: newNodeTitle.trim(),
        parent_id: parentNodeId,
        question_id: questionId,
      });
      
      // Update nodes list
      setNodes(prev => [...prev, response.data]);
      
      // Update parent's children_ids
      setNodes(prev => prev.map(n => {
        if (n.node_id === parentNodeId) {
          return {
            ...n,
            children_ids: [...(n.children_ids || []), response.data.node_id],
          };
        }
        return n;
      }));
      
      setShowAddNode(false);
      toast.success('Node added!');
      
      // Refresh to get accurate data
      fetchQuestionData();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to add node';
      toast.error(errorMessage);
    } finally {
      setAddingNode(false);
    }
  };

  const handleVote = useCallback(async (nodeId, voteType) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to vote');
      return;
    }
    
    try {
      const response = await votesAPI.vote({ node_id: nodeId, vote_type: voteType });
      
      // Update local state
      setNodes(prev => prev.map(n => {
        if (n.node_id === nodeId) {
          return {
            ...n,
            upvotes: response.data.upvotes,
            downvotes: response.data.downvotes,
            vote_score: response.data.vote_score,
          };
        }
        return n;
      }));
      
      // Update user vote
      setUserVotes(prev => {
        const newVotes = { ...prev };
        if (prev[nodeId] === voteType) {
          delete newVotes[nodeId];
        } else {
          newVotes[nodeId] = voteType;
        }
        return newVotes;
      });
    } catch (error) {
      toast.error('Failed to vote');
    }
  }, [isAuthenticated]);

  const handleComment = useCallback(async (nodeId) => {
    setSelectedNodeId(nodeId);
    setShowComments(true);
    setLoadingComments(true);
    
    try {
      const response = await commentsAPI.getByNode(nodeId);
      setComments(response.data);
    } catch (error) {
      toast.error('Failed to load comments');
    } finally {
      setLoadingComments(false);
    }
  }, []);

  const handleSubmitComment = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to comment');
      return;
    }
    
    if (!newComment.trim()) return;
    
    try {
      const response = await commentsAPI.create({
        node_id: selectedNodeId,
        content: newComment.trim(),
      });
      
      setComments(prev => [response.data, ...prev]);
      setNewComment('');
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await commentsAPI.delete(commentId);
      setComments(prev => prev.filter(c => c.comment_id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleViewContent = useCallback((nodeId) => {
    const node = nodes.find(n => n.node_id === nodeId);
    if (node) {
      setSelectedNode(node);
      setShowContent(true);
    }
  }, [nodes]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="spinner" data-testid="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!question) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-slate-700">Mindmap not found</h2>
            <Button onClick={() => navigate('/')} className="mt-4">
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const selectedNodeData = nodes.find(n => n.node_id === selectedNodeId);

  const currentUrl = `${window.location.origin}/q/${questionId}`;

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
      {/* SEO */}
      <SEOHead
        title={question.title}
        description={question.description || `Explore "${question.title}" - a collaborative mindmap with ${question.node_count} nodes and ${question.contributor_count} contributors on Clarities.`}
        url={currentUrl}
        type="article"
        author={question.author_name}
        keywords={`mindmap, ${question.title}, knowledge sharing, collaborative learning`}
      />
      <MindmapStructuredData question={question} nodes={nodes} url={currentUrl} />
      
      <Header />
      
      {/* Login Prompt Banner (for non-authenticated users) */}
      {!isAuthenticated && (
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <LogIn className="w-5 h-5" />
              <span className="text-sm font-medium">
                Sign in to add nodes, vote, and contribute to this mindmap
              </span>
            </div>
            <Button
              onClick={() => navigate('/login')}
              size="sm"
              className="bg-white text-orange-600 hover:bg-orange-50 font-medium"
              data-testid="login-prompt-btn"
            >
              Sign In to Contribute
            </Button>
          </div>
        </div>
      )}
      
      {/* Mindmap Viewer - Full Screen with bottom margin for info panel */}
      <div className="relative flex-1 flex" style={{ minHeight: '600px' }} data-testid="question-detail-page">
        {/* Main Mindmap Area */}
        <div className="flex-1 relative mb-[140px] sm:mb-[120px]">
          <div className="absolute inset-0">
            <MindmapViewer
              nodes={nodes}
              rootNodeId={question.root_node_id}
              questionId={questionId}
              highlightNodeId={highlightNodeId}
              onAddChild={handleAddChild}
              onVote={handleVote}
              onComment={handleComment}
              onViewContent={handleViewContent}
              userVotes={userVotes}
            />
          </div>
          
          {/* Floating Back Button */}
          <button
            onClick={() => navigate('/')}
            className="absolute top-4 left-4 z-10 bg-white shadow-lg rounded-full p-2 text-slate-600 hover:text-slate-900 hover:shadow-xl transition-all"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        </div>
        
        {/* Related Mindmaps Sidebar */}
        <div className="hidden lg:block w-72 border-l border-slate-200 bg-slate-50/50 overflow-y-auto p-4 mb-[120px]">
          <RelatedMindmaps questionId={questionId} />
        </div>
      </div>
      
      {/* Fixed Bottom Question Info Panel */}
      <div className="fixed bottom-0 left-0 right-0 z-20 bg-white border-t border-slate-200 shadow-lg" data-testid="question-info-panel">
        <div className="max-w-7xl mx-auto">
          <Tabs value={activeInfoTab} onValueChange={setActiveInfoTab} className="w-full">
            {/* Tab Navigation - Responsive layout */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-2 gap-2 border-b border-slate-100">
              <TabsList className="bg-slate-100/80 h-8 sm:h-9 w-full sm:w-auto justify-start">
                <TabsTrigger 
                  value="overview" 
                  className="text-xs px-2 sm:px-3 data-[state=active]:bg-white"
                  data-testid="tab-overview"
                >
                  <Info className="w-3.5 h-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">Overview</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="contributors" 
                  className="text-xs px-2 sm:px-3 data-[state=active]:bg-white"
                  data-testid="tab-contributors"
                >
                  <Users className="w-3.5 h-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">Contributors</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="related" 
                  className="text-xs px-2 sm:px-3 data-[state=active]:bg-white lg:hidden"
                  data-testid="tab-related"
                >
                  <Compass className="w-3.5 h-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">Related</span>
                </TabsTrigger>
              </TabsList>
              
              {/* Actions - Always Visible */}
              <div className="flex items-center gap-2 justify-end">
                <FollowButton questionId={questionId} variant="icon" />
                <ShareButton 
                  url={currentUrl}
                  title={question.title}
                  description={question.description || `Explore this collaborative mindmap on Clarities`}
                  type="mindmap"
                />
                {isAuthenticated ? (
                  <Button
                    onClick={() => handleAddChild(question.root_node_id)}
                    size="sm"
                    className="btn-accent flex items-center gap-1.5"
                    data-testid="add-node-btn"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Add Node</span>
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate('/login')}
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1.5 border-orange-300 text-orange-600 hover:bg-orange-50"
                    data-testid="login-to-contribute-btn"
                  >
                    <LogIn className="w-4 h-4" />
                    <span className="hidden sm:inline">Sign In</span>
                  </Button>
                )}
              </div>
            </div>
            
            {/* Tab Content */}
            <div className="px-4 py-3">
              {/* Overview Tab */}
              <TabsContent value="overview" className="mt-0 space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  {/* Title and Description */}
                  <div className="flex-1 min-w-0">
                    <h1 className="text-lg font-bold text-slate-900 leading-tight" data-testid="question-title">
                      {question.title}
                    </h1>
                    {question.description && (
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2" data-testid="question-description">
                        {question.description}
                      </p>
                    )}
                  </div>
                  
                  {/* Meta Info */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 sm:flex-col sm:items-end sm:gap-1">
                    <div className="flex items-center gap-1.5" data-testid="question-author">
                      <User className="w-3.5 h-3.5" />
                      <span>by <span className="font-medium text-slate-700">{question.author_name}</span></span>
                    </div>
                    <div className="flex items-center gap-1.5" data-testid="question-date">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{formatDistanceToNow(new Date(question.created_at), { addSuffix: true })}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1" data-testid="question-node-count">
                        <GitBranch className="w-3.5 h-3.5" />
                        {question.node_count} nodes
                      </span>
                      <span className="flex items-center gap-1" data-testid="question-contributor-count">
                        <Users className="w-3.5 h-3.5" />
                        {question.contributor_count} contributors
                      </span>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              {/* Contributors Tab */}
              <TabsContent value="contributors" className="mt-0">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-medium text-sm">
                      {question.author_name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{question.author_name}</p>
                      <p className="text-xs text-slate-500">Original author</p>
                    </div>
                  </div>
                  {question.contributor_count > 1 && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <div className="flex -space-x-2">
                        {[...Array(Math.min(question.contributor_count - 1, 4))].map((_, i) => (
                          <div 
                            key={`contributor-${i}`}
                            className="w-7 h-7 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-xs font-medium text-slate-600"
                          >
                            +
                          </div>
                        ))}
                      </div>
                      <span>+{question.contributor_count - 1} more contributors</span>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              {/* Related Tab (Mobile only) */}
              <TabsContent value="related" className="mt-0 lg:hidden">
                <div className="max-h-32 overflow-y-auto">
                  <RelatedMindmaps questionId={questionId} compact />
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>

      {/* Add Node Dialog */}
      <Dialog open={showAddNode} onOpenChange={setShowAddNode}>
        <DialogContent data-testid="add-node-dialog">
          <DialogHeader>
            <DialogTitle>Add a New Node</DialogTitle>
            <DialogDescription>
              Add a branch to this mindmap. Keep it concise and relevant.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Input
                placeholder="Enter node title..."
                value={newNodeTitle}
                onChange={(e) => setNewNodeTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmitNode()}
                data-testid="new-node-title-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAddNode(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitNode}
                disabled={addingNode || !newNodeTitle.trim()}
                className="flex-1 btn-accent"
                data-testid="submit-node-btn"
              >
                {addingNode ? <div className="spinner w-4 h-4"></div> : 'Add Node'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Comments Sheet */}
      <Sheet open={showComments} onOpenChange={setShowComments}>
        <SheetContent className="w-full sm:max-w-md" data-testid="comments-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Comments
              {selectedNodeData && (
                <span className="text-sm font-normal text-slate-500">
                  on &ldquo;{selectedNodeData.title}&rdquo;
                </span>
              )}
            </SheetTitle>
          </SheetHeader>
          
          <div className="mt-6 flex flex-col h-[calc(100vh-10rem)]">
            {/* Comment Input with Mention support */}
            {isAuthenticated && (
              <div className="flex gap-2 mb-4">
                <div className="flex-1">
                  <MentionInput
                    value={newComment}
                    onChange={setNewComment}
                    onSubmit={handleSubmitComment}
                    placeholder="Add a comment... (use @name to mention)"
                    users={mentionUsers}
                    data-testid="comment-input"
                  />
                </div>
                <Button
                  size="icon"
                  onClick={handleSubmitComment}
                  disabled={!newComment.trim()}
                  className="btn-accent self-start mt-0"
                  data-testid="submit-comment-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            {/* Comments List */}
            <div className="flex-1 overflow-y-auto space-y-4">
              {loadingComments ? (
                <div className="flex justify-center py-8">
                  <div className="spinner"></div>
                </div>
              ) : comments.length > 0 ? (
                comments.map((comment) => (
                  <div 
                    key={comment.comment_id} 
                    className="bg-slate-50 rounded-lg p-4"
                    data-testid={`comment-${comment.comment_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">
                          {comment.author_picture ? (
                            <img src={comment.author_picture} alt="" className="w-full h-full rounded-full" />
                          ) : (
                            comment.author_name?.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm text-slate-900">{comment.author_name}</p>
                          <p className="text-xs text-slate-400">
                            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </div>
                      {user?.user_id === comment.author_id && (
                        <button
                          onClick={() => handleDeleteComment(comment.comment_id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          data-testid={`delete-comment-${comment.comment_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-slate-700">{renderTextWithMentions(comment.content)}</p>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No comments yet</p>
                  {isAuthenticated && (
                    <p className="text-sm mt-1">Be the first to comment!</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Content Sheet (One-pager) */}
      <Sheet open={showContent} onOpenChange={setShowContent}>
        <SheetContent className="w-full sm:max-w-2xl" data-testid="content-sheet">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {selectedNode?.title}
            </SheetTitle>
          </SheetHeader>
          
          <div className="mt-6">
            {selectedNode?.content ? (
              <RichTextViewer content={selectedNode.content} />
            ) : (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>No detailed content yet</p>
                {isAuthenticated && user?.user_id === selectedNode?.author_id && (
                  <Button
                    onClick={() => navigate(`/node/${selectedNode.node_id}`)}
                    className="mt-4 btn-accent"
                  >
                    Add Content
                  </Button>
                )}
              </div>
            )}
            
            {selectedNode?.content && isAuthenticated && user?.user_id === selectedNode?.author_id && (
              <div className="mt-6">
                <Button
                  onClick={() => navigate(`/node/${selectedNode.node_id}`)}
                  variant="outline"
                >
                  Edit Content
                </Button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
